import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { GalleryItem } from '../types/salonTypes';
import apiService from '../utils/api';

// API URL sabiti
const API_URL = 'https://api.345.tr/v1.3';

export interface GalleryPageProps {
  website: {
    salonInfo: any;
    content: {
      galleryDescription?: string;
    };
  };
}

const GalleryPage: React.FC<GalleryPageProps> = ({ website }) => {
  const [activeCategory, setActiveCategory] = useState<'salon' | 'musteri'>('salon');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [galleryData, setGalleryData] = useState<{
    salon: GalleryItem[],
    musteri: GalleryItem[]
  }>({
    salon: [],
    musteri: []
  });
  
  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        setLoading(true);
        if (!website || !website.salonInfo || !website.salonInfo._id) {
          setError('Salon bilgisi bulunamadı');
          return;
        }

        // Salon ve müşteri fotoğraflarını getir
        const salonResponse = await apiService.getSalonPhotos(website.salonInfo._id, 'salon');
        const customerResponse = await apiService.getSalonPhotos(website.salonInfo._id, 'customer');
        
        // Salon fotoğraflarını işle
        const salonPhotos = salonResponse.success && salonResponse.photos 
          ? salonResponse.photos.map((photo: any, index: number) => {
              const photoUrl = photo.photoUrl.startsWith('http') 
                ? photo.photoUrl 
                : `${API_URL}${photo.photoUrl}`;
              
              return {
                id: photo._id || `salon${index}`,
                image: photoUrl,
                title: `Salon Fotoğrafı ${index + 1}`,
                description: `Salon fotoğrafı ${index + 1}`
              };
            })
          : [];
          
        // Müşteri fotoğraflarını işle
        const customerPhotos = customerResponse.success && customerResponse.photos 
          ? customerResponse.photos.map((photo: any, index: number) => {
              const photoUrl = photo.photoUrl.startsWith('http') 
                ? photo.photoUrl 
                : `${API_URL}${photo.photoUrl}`;
              
              return {
                id: photo._id || `musteri${index}`,
                image: photoUrl,
                title: `Müşteri Çalışması ${index + 1}`,
                description: `Müşteri çalışması ${index + 1}`
              };
            })
          : [];
          
        setGalleryData({
          salon: salonPhotos,
          musteri: customerPhotos
        });
        
        console.log('Galeri verileri yüklendi:', { salonCount: salonPhotos.length, customerCount: customerPhotos.length });
      } catch (err) {
        console.error('Galeri verileri yüklenirken hata:', err);
        setError('Galeri fotoğrafları yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGalleryData();
  }, [website]);
  
  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };
  
  const closeModal = () => {
    setSelectedImage(null);
  };

  // Açıklama metnini website.content.galleryDescription'dan veya varsayılan metinden al
  const galleryDescription = website?.content?.galleryDescription || 'Salonumuzdan görüntüler ve yaptığımız çalışmalardan örnekler';

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingMessage>Fotoğraflar yükleniyor...</LoadingMessage>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </ErrorContainer>
    );
  }

  return (
    <GalleryPageContainer>
      <PageHeader>
        <div className="container">
          <PageTitle>Galeri</PageTitle>
          <PageDescription>{galleryDescription}</PageDescription>
        </div>
      </PageHeader>

      <section className="container">
        <GalleryNavigation>
          <CategoryTab 
            active={activeCategory === 'salon'} 
            onClick={() => setActiveCategory('salon')}
          >
            Salon Fotoğrafları
          </CategoryTab>
          <CategoryTab 
            active={activeCategory === 'musteri'} 
            onClick={() => setActiveCategory('musteri')}
          >
            Müşteri Çalışmaları
          </CategoryTab>
        </GalleryNavigation>
        
        {activeCategory === 'salon' ? (
          <GalleryGrid>
            {galleryData.salon.length > 0 ? (
              galleryData.salon.map((item: GalleryItem) => (
                <GalleryItemContainer key={item.id} onClick={() => handleImageClick(item.image)}>
                  <GalleryImage src={item.image} alt={item.title} />
                </GalleryItemContainer>
              ))
            ) : (
              <NoDataMessage>Salon fotoğrafı bulunamadı.</NoDataMessage>
            )}
          </GalleryGrid>
        ) : (
          <GalleryGrid>
            {galleryData.musteri.length > 0 ? (
              galleryData.musteri.map((item: GalleryItem) => (
                <GalleryItemContainer key={item.id} onClick={() => handleImageClick(item.image)}>
                  <GalleryImage src={item.image} alt={item.title} />
                </GalleryItemContainer>
              ))
            ) : (
              <NoDataMessage>Müşteri çalışması fotoğrafı bulunamadı.</NoDataMessage>
            )}
          </GalleryGrid>
        )}
      </section>

      {selectedImage && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>&times;</CloseButton>
            <ModalImage src={selectedImage} alt="Büyük görüntü" />
          </ModalContent>
        </Modal>
      )}
    </GalleryPageContainer>
  );
};

const GalleryPageContainer = styled.div`
  margin-top: 80px;
`;

const PageHeader = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}30, ${({ theme }) => theme.colors.secondary}30);
  padding: 80px 0 50px;
  text-align: center;
`;

const PageTitle = styled.h1`
  margin-bottom: 20px;
  font-size: 2.5rem;
  font-weight: 700;
  position: relative;
  display: inline-block;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    margin: 15px auto 0;
    border-radius: 3px;
  }
`;

const PageDescription = styled.p`
  color: #666;
  font-size: 1.1rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const GalleryNavigation = styled.div`
  display: flex;
  justify-content: center;
  margin: 40px 0;
  gap: 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`;

const CategoryTab = styled.button<{ active: boolean }>`
  padding: 12px 25px;
  border: none;
  border-radius: 30px;
  background: ${({ active, theme }) => 
    active 
      ? `linear-gradient(45deg, ${theme.colors.primary}, ${theme.colors.secondary})` 
      : '#f5f5f5'};
  color: ${({ active }) => active ? 'white' : '#666'};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 60px;
`;

const GalleryItemContainer = styled.div`
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  aspect-ratio: 1 / 1;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    
    img {
      transform: scale(1.1);
    }
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  color: #333;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }
`;

const ModalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  margin-top: 80px;
`;

const LoadingMessage = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primary};
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  margin-top: 80px;
`;

const ErrorMessage = styled.p`
  font-size: 1.2rem;
  color: #e74c3c;
  text-align: center;
`;

const NoDataMessage = styled.p`
  grid-column: 1 / -1;
  text-align: center;
  color: #666;
  font-size: 1.1rem;
  padding: 40px;
  background: #f8f9fa;
  border-radius: 10px;
`;

export default GalleryPage;